import { create } from 'zustand';
import { API } from 'aws-amplify';
import { getDimensionValue } from '../../routes/leaderboard/Leaderboard.utils';
import useOrgStore from './useOrgStore';
import getUserAvatar from '../../common/utils/getUserAvatar';

export const GET_KPI_VALUES_QUERY = `
  query GetKPIValuesV6(
    $dimensionType: String!
    $dimensionTimeframe: String!
    $dimensionValue: String!
    $nextToken: String
    $limit: Int
    $sortColumn: String
    $sortOrder: String
    $circleLevelID: String
    $circleID: String
    $viewID: String
    $parentCircleID: String
  ) {
    getKPIValuesV6(
      dimensionType: $dimensionType
      dimensionTimeframe: $dimensionTimeframe
      dimensionValue: $dimensionValue
      nextToken: $nextToken
      limit: $limit
      sortColumn: $sortColumn
      sortOrder: $sortOrder
      circleLevelID: $circleLevelID
      circleID: $circleID
      viewID: $viewID
      parentCircleID: $parentCircleID
    )
  }
`;

const CACHE_DURATION = 1000 * 60; // 1 minute

export interface KPI {
  [key: string]: string;
}
export interface Result {
  id: string;
  rank: number;
  name: string;
  kpis: KPI;
  imageName?: string;
  imageType?: string;
  avatar?: string;
}
export interface Highlight {
  id: string;
  abbreviation: string;
  change: string;
  pastTotal: string;
  title: string;
  total: string;
}
export interface KPIValues {
  totalResults: number;
  nextToken: string;
  nextTokensList: string[];
  highlightsData: {
    [key: string]: Highlight;
  };
  kpiList: KPI;
  results: Result[];
}

export interface GetKPIValuesResponse {
  data: {
    getKPIValuesV6: string;
  };
}

export type DimensionTimeframe = 'D' | 'W' | 'M' | 'Y';

export type DimensionType = 'circle' | 'person';

export interface LeaderboardFilters {
  dimensionType: DimensionType;
  dimensionTimeframe: DimensionTimeframe;
  dimensionValue: string;
  nextToken: string;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  circleLevelID: string;
  circleID: string;
  parentCircleID: string;
  viewID: string;
}

export type LeaderboardFilterKeys = keyof LeaderboardFilters;
export interface LeaderboardStackView {
  viewID?: string;
  dimensionType?: DimensionType;
  parentCircleID?: string;
  parentCircleName?: string;
  circleID?: string;
  circleName?: string;
  circleLevelID?: string;
  circleLevelName?: string;
}

interface LeaderboardStore {
  isLoading: boolean;
  leaderboardFilters: LeaderboardFilters | null;
  updateFilters: (filtersUpdates: Partial<LeaderboardFilters>) => void;
  leaderboardCachedData: {
    [key: string]: {
      data: any;
      timestamp: string;
    };
  };
  applyView: (viewID: string) => void;
  leaderboardViews: any[] | null;
  setLeaderboardViews: (leaderboardViews: any[]) => void;
  generateFilterKey: (leaderboardFilters: LeaderboardFilters) => string;
  nextTokens: string[];
  currentNextToken: string;
  viewsStack: LeaderboardStackView[];
  setViewsStack: (
    viewsStack: LeaderboardStackView[],
    filterOverrides?: Partial<LeaderboardFilters>
  ) => void;
  fetchLeaderboardData: (
    leaderboardFilters: LeaderboardFilters,
    forceFetch?: boolean
  ) => void;
  leaderboardData: any;
  resetStore: () => void;
  //   resetFilters: () => void;
}

const initialFilters: Partial<LeaderboardFilters> = {
  dimensionType: 'circle',
  dimensionTimeframe: 'M',
  dimensionValue: '',
  nextToken: '',
  limit: 50,
  sortBy: '--first--',
  sortOrder: 'desc',
};

const orderedKeys: LeaderboardFilterKeys[] = [
  'dimensionType',
  'dimensionTimeframe',
  'dimensionValue',
  'nextToken',
  'limit',
  'sortBy',
  'sortOrder',
  'circleLevelID',
  'circleID',
  'parentCircleID',
  'viewID',
];

const useLeaderboardStore = create<LeaderboardStore>((set, get) => ({
  isLoading: false,
  leaderboardFilters: null,
  leaderboardData: {},
  leaderboardCachedData: {},
  leaderboardViews: null,
  currentNextToken: '',
  nextTokens: [],
  updateFilters: (filtersUpdates: Partial<LeaderboardFilters>) => {
    const updatedFilters = {
      ...get().leaderboardFilters,
      ...filtersUpdates,
    } as LeaderboardFilters;
    set({ leaderboardFilters: updatedFilters });
    get().fetchLeaderboardData(updatedFilters);
  },
  applyView: (viewID: string) => {
    const filterOverrides: Partial<LeaderboardFilters> = {
      sortBy: '--first--',
      sortOrder: 'desc',
      viewID,
    };

    if (viewID === '') {
      const newStack: LeaderboardStackView[] = get().viewsStack.map(
        (view: LeaderboardStackView) => {
          return {
            ...view,
            ...filterOverrides,
          };
        }
      );
      get().setViewsStack(newStack, { nextToken: '', viewID: '' });
      return;
    }
    const circleLevels = useOrgStore.getState().circleLevels;
    const leaderboardFilters = get().leaderboardFilters;
    const view = get().leaderboardViews?.find(
      (view: any) => view.id === viewID
    );
    const { preferences: viewPreferences } = view;
    const { viewLevels } = viewPreferences;

    const newViewsStack: LeaderboardStackView[] = [];
    if (viewLevels.length > 0) {
      let lastParentName: string = '';
      let lastParentID: string = '';
      viewLevels.forEach((viewLevel: any, index: number) => {
        const circleLevel = circleLevels[index];
        const newView: Partial<LeaderboardStackView> = {
          viewID,
          dimensionType: 'circle',
          circleLevelID: circleLevel.id,
          circleLevelName: circleLevel.name,
          parentCircleID: '',
          parentCircleName: '',
          circleID: '',
          circleName: '',
        };
        if (index > 0) {
          const parentCircleLevel = circleLevels[index - 1];
          newView.circleID = viewLevel.id;
          newView.circleName = viewLevel.label;
          newView.parentCircleID = parentCircleLevel.id;
          newView.parentCircleName = parentCircleLevel.name;
          lastParentName = viewLevel.name;
          lastParentID = viewLevel.id;
        }
        newViewsStack.push(newView);
      });
      const newCurrentView: LeaderboardStackView = {
        viewID,
        dimensionType: viewPreferences.dimensionType,
        circleID: viewPreferences.circleID,
        circleLevelID: viewPreferences.circleLevelID,
        circleName: viewPreferences.circleName,
        circleLevelName: circleLevels[viewLevels.length - 1].name,
        parentCircleID: lastParentID,
        parentCircleName: lastParentName,
      };
      newViewsStack.push(newCurrentView);
      get().setViewsStack(newViewsStack, {
        dimensionTimeframe: viewPreferences.dimensionTimeframe,
        dimensionValue: getDimensionValue(
          viewPreferences.dimensionTimeframe ||
            leaderboardFilters?.dimensionTimeframe
        ),
        nextToken: '',
        ...filterOverrides,
      });
    } else {
      const newCurrentView = {
        viewID,
        dimensionType: viewPreferences.dimensionType,
        parentCircleID: '',
        parentCircleName: '',
        circleID: viewPreferences.circleID,
        circleName: '',
        circleLevelID: circleLevels[0].id,
        circleLevelName: circleLevels[0].name,
      };
      newViewsStack.push(newCurrentView);
      get().setViewsStack(newViewsStack, {
        dimensionTimeframe: viewPreferences.dimensionTimeframe,
        dimensionValue: getDimensionValue(
          viewPreferences.dimensionTimeframe ||
            leaderboardFilters?.dimensionTimeframe
        ),
        nextToken: '',
        ...filterOverrides,
      });
    }
  },

  setLeaderboardViews: (leaderboardViews: any[]) => {
    set({ leaderboardViews });
  },
  generateFilterKey: (leaderboardFilters: LeaderboardFilters) => {
    return orderedKeys.map((key) => leaderboardFilters[key] || '*').join('-');
  },
  viewsStack: [],
  setViewsStack: (
    viewsStack: LeaderboardStackView[],
    filterOverrides: Partial<LeaderboardFilters> = {}
  ) => {
    const currentView = viewsStack[viewsStack.length - 1];

    const currentFilters: Partial<LeaderboardFilters> =
      get().leaderboardFilters || initialFilters;
    const filteredView = Object.keys(currentView).reduce((obj, key) => {
      if (orderedKeys.includes(key as LeaderboardFilterKeys)) {
        obj[key as keyof LeaderboardFilters] = currentView[
          key as keyof LeaderboardStackView
        ] as any;
      }
      return obj;
    }, {} as Partial<LeaderboardFilters>);

    // Merge with the current filters
    const updatedFilters = {
      ...currentFilters,
      ...filteredView,
      ...filterOverrides,
      nextTokens: [],
    } as LeaderboardFilters;

    // Update the store
    set({ leaderboardFilters: updatedFilters, viewsStack });
    get().fetchLeaderboardData(updatedFilters);
  },
  fetchLeaderboardData: async (
    leaderboardFilters: LeaderboardFilters,
    forceFetch: boolean = false
  ) => {
    const {
      dimensionType,
      dimensionTimeframe,
      circleLevelID,
      circleID,
      parentCircleID,
      viewID,
      sortBy,
      sortOrder,
      limit,
      nextToken,
      dimensionValue,
    } = leaderboardFilters;

    const variables = {
      dimensionType,
      dimensionTimeframe,
      dimensionValue: dimensionValue || getDimensionValue(dimensionTimeframe),
      nextToken,
      limit,
      sortColumn: sortBy,
      sortOrder,
      circleLevelID,
      circleID,
      parentCircleID,
      viewID,
    };
    set({
      leaderboardFilters: {
        ...leaderboardFilters,
        dimensionValue: dimensionValue || getDimensionValue(dimensionTimeframe),
      },
    });

    const dataKey = get().generateFilterKey(leaderboardFilters);
    try {
      let results;

      const cacheEntry = get().leaderboardCachedData[dataKey];
      const isCacheValid =
        cacheEntry &&
        Date.now() - new Date(cacheEntry.timestamp).getTime() < CACHE_DURATION;

      // Set loading to true when fetching data, regardless of cache
      set({ isLoading: true });

      if (isCacheValid && !forceFetch) {
        results = cacheEntry.data;
      } else {
        const apiResult = (await API.graphql({
          query: GET_KPI_VALUES_QUERY,
          variables,
        })) as GetKPIValuesResponse;
        results = apiResult.data.getKPIValuesV6;
        set({
          leaderboardCachedData: {
            ...get().leaderboardCachedData,
            [dataKey]: { data: results, timestamp: new Date().toISOString() },
          },
        });
      }
      const parsedResult = JSON.parse(results);

      // Simple avatar pre-loading during data fetch
      if (parsedResult.results && parsedResult.results.length > 0) {
        const resultsWithAvatars = await Promise.all(
          parsedResult.results.map(async (result: Result) => {
            if (result.imageName) {
              try {
                const avatar = await getUserAvatar(
                  result.imageName,
                  result.imageType || '',
                  true
                );
                return { ...result, avatar };
              } catch (error) {
                console.warn('Failed to load avatar for:', result.name, error);
                return { ...result, avatar: '' };
              }
            }
            return { ...result, avatar: '' };
          })
        );
        parsedResult.results = resultsWithAvatars;
      }

      set({ leaderboardData: parsedResult });
      if (leaderboardFilters.sortBy === '--first--') {
        const resolvedSortBy = Object.keys(parsedResult.kpiList)[0];

        const updatedFilters = {
          ...leaderboardFilters,
          sortBy: resolvedSortBy,
        };

        set({ leaderboardFilters: updatedFilters });

        const resolvedDataKey = get().generateFilterKey(updatedFilters);
        set({
          leaderboardCachedData: {
            ...get().leaderboardCachedData,
            [resolvedDataKey]: {
              data: results,
              timestamp: new Date().toISOString(),
            },
          },
        });
      }
    } catch (error) {
      console.error('*LB1* - error', error);
    } finally {
      set({ isLoading: false });
    }
  },
  resetStore: () => {
    set({
      leaderboardFilters: null,
      leaderboardData: {},
      leaderboardViews: null,
    });
  },
}));

export default useLeaderboardStore;
